import { FC, Fragment, useRef, useEffect } from 'react'
import { CheckSquare, ArrowLeft } from 'react-feather'
import { useQueryClient } from '@tanstack/react-query'
import InfiniteScroll from 'react-infinite-scroll-component'
import { formatDistance } from 'date-fns'
import { useHistory } from 'react-router-dom'
import kebabCase from 'lodash.kebabcase'
import clsx from 'clsx'

import {
  Empty,
  useNotifications,
  Spin,
  Button,
  useShowMessage,
  useGlobalState,
  useGetNotificationCount,
} from '@pepper/ui'
import {
  editNotificationStatus,
  editNotificationsStatus,
} from '@pepper/utils/src/api/notifications/notifications'

import { NOTIFICATION_TYPE } from 'utils/constants/notification_type'
import { useSelectedCompanyId } from 'utils/functions/misc'

import styles from './_.module.css'

const MAX_PAGE_SIZE = 25

const Notifications: FC<{
  onClose: () => void
  unseenCount: number
  isMobileView?: boolean
}> = ({ onClose, unseenCount, isMobileView = false }) => {
  const notificationBody = useRef<HTMLElement>(null)
  const showMessage = useShowMessage()
  const history = useHistory()
  const companyId = useSelectedCompanyId()
  const queryClient = useQueryClient()
  const { pendingSidebarCountKeys } = useGlobalState()

  const notificationCount = useGetNotificationCount({ data: { companyId } })
  const { fetchMore, canFetchMore, data, totalRecords, refetch } =
    useNotifications('notification', { company_id: companyId }, MAX_PAGE_SIZE)

  /** update notification list on project creation */
  useEffect(() => {
    if (pendingSidebarCountKeys.includes('allProjectsCount')) {
      refetch()
    }
  }, [pendingSidebarCountKeys, refetch])

  const handleNotificationRead = async (id: string): Promise<void> => {
    try {
      await editNotificationStatus({
        id,
        read_status: true,
      })
      refetch()
      queryClient.invalidateQueries(['notifications', 'count'])
    } catch (error) {
      showMessage({ type: 'error', message: error.message })
    }
  }

  const handleNotificationAllRead = async (): Promise<void> => {
    try {
      await editNotificationsStatus({
        read_status: true,
      })
      refetch()
      queryClient.invalidateQueries(['notifications', 'count'])
    } catch (error) {
      showMessage({ type: 'error', message: error.message })
    }
  }

  const notificationClickHandler = (
    id: string | null,
    url: string | null,
    isRead: number
  ) => {
    url && history.push(url)
    id && isRead === 0 && handleNotificationRead(id)
    onClose()
  }

  return (
    <div className={styles.notificationContainer}>
      <div className={styles.notificationHeader}>
        <div className={styles.titleContainer}>
          {isMobileView && <ArrowLeft size={18} onClick={onClose} />}
          <span className={styles.title}>Notifications</span>
          {unseenCount > 0 && (
            <span className={styles.count}>{unseenCount}</span>
          )}
        </div>
        {notificationCount.data?.total ? (
          <Button
            type="link"
            className={styles.markAllAsReadContainer}
            onClick={handleNotificationAllRead}
          >
            <span>Mark all as read</span>
            <CheckSquare size={14} />
          </Button>
        ) : (
          ''
        )}
      </div>
      <section
        className={clsx(styles.notificationBody, styles.customScrollBar)}
        ref={notificationBody}
        id="js-notification-body"
      >
        <InfiniteScroll
          hasMore={canFetchMore ?? false}
          next={fetchMore}
          dataLength={totalRecords}
          scrollableTarget="js-notification-body"
          loader={<Spin className="w-100" />}
        >
          {totalRecords === 0 && (
            <div className={styles.emptyContainer}>
              <Empty description="No Notification" />
            </div>
          )}
          {data.flatMap((item, i) => (
            <Fragment key={i}>
              {item.records.map(notification => (
                <NotificationCard
                  key={notification.id}
                  item={notification}
                  onClick={notificationClickHandler}
                />
              ))}
            </Fragment>
          ))}
        </InfiniteScroll>
      </section>
    </div>
  )
}

const NotificationCard: FC<{
  item: UserNotification
  onClick: (id: string | null, url: string | null, isRead: number) => void
}> = ({ item, onClick }) => {
  const notificationType = item.type || item.payload?.type
  const metadata =
    notificationType &&
    NOTIFICATION_TYPE(item.payload?.id, 'client')[notificationType]
      ? NOTIFICATION_TYPE(item.payload?.id, 'client')[notificationType]
      : {
          id: null,
          title: '',
          url: '',
          urlTitle: '',
          color: 'green',
          className: '',
        }

  /** Get highlighted word */
  const getHighlightedString = (subTitle: string, title: string) => {
    const highlightStartIndex = title.indexOf(subTitle)
    const highlightLastIndex = highlightStartIndex + subTitle.length

    const prevPart = title.slice(0, highlightStartIndex)
    const highlightPart = title.slice(highlightStartIndex, highlightLastIndex)
    const nextPart = title.slice(highlightLastIndex, title.length)

    if (highlightStartIndex === -1)
      return <p className={styles.message}>{title}</p>

    return (
      <p className={styles.message}>
        {prevPart}
        <strong>{highlightPart}</strong>
        {nextPart}
      </p>
    )
  }

  const dataId = kebabCase(
    `business-notifications-${metadata?.id}-notification-card`
  )

  return (
    <div
      className={clsx(
        styles.notificationCardContainer,
        item.is_read && styles.readMessageContainer
      )}
      onClick={() => onClick(item.id, metadata.url, item.is_read)}
      onKeyPress={() => onClick(item.id, metadata.url, item.is_read)}
      role="button"
      tabIndex={0}
      data-id={dataId}
    >
      <div className={styles.notificationDetails}>
        <div>
          <div className={styles.notificationTag}>
            {metadata?.title ? (
              <span className={metadata?.className}>{metadata.title}</span>
            ) : (
              ''
            )}
          </div>
          {getHighlightedString(item.subtitle, item.title)}
        </div>
        {['assignment_delivered', 'project_approval_pending'].includes(
          item.type || ''
        ) ? (
          <Button
            onClick={() => onClick(item.id, metadata.url, item.is_read)}
            type="link"
            className={styles.reviewAssignmentBtn}
          >
            Review Assignment
          </Button>
        ) : (
          ''
        )}
        <span className={styles.notificationTimestamp}>
          {`${formatDistance(new Date(), new Date(item.timestamp))} ago`}
          {!item.is_read ? <span className={styles.unreadNotification} /> : ''}
        </span>
      </div>
    </div>
  )
}

export default Notifications
